import React from 'react';
import { QRCodeSVG } from 'qrcode.react';
import { MapPin, Phone, Mail, Home, Info, Star, Check, Calendar } from 'lucide-react';

// URLs de Cloudflare
const CLOUDFLARE_PROPERTIES_URL = 'https://pub-e51257e7234a40ea96269a0ac4d1c6ab.r2.dev';
const CLOUDFLARE_PROFILES_URL = 'https://pub-2a10bd66114a4441b95e9249adee01cf.r2.dev';

// Componente para imágenes de propiedades
const PropertyImage = ({ url, alt, className, objectFit = "cover" }) => {
  if (!url) return (
    <img
      src="/default-property-image.jpg"
      alt={alt}
      className={className}
      style={{ objectFit }}
    />
  );

  const fullUrl = url.startsWith('http') ? url : `${CLOUDFLARE_PROPERTIES_URL}/${url}`;

  return (
    <img
      src={fullUrl}
      alt={alt}
      className={className}
      style={{ objectFit }}
      onError={(e) => {
        console.error('Error loading property image:', fullUrl);
        e.target.src = '/default-property-image.jpg';
      }}
    />
  );
};

const ElegantBlueFlyer = ({ property }) => {
  const formatPrice = (price) => {
    return new Intl.NumberFormat('es-PE').format(price);
  };

  const formatTitle = (title) => {
    if (!title) return [];
    const words = title.split(' ');
    let lines = [];
    let currentLine = '';

    words.forEach(word => {
      if ((currentLine + ' ' + word).length <= 12) {
        currentLine += (currentLine ? ' ' : '') + word;
      } else {
        if (currentLine) lines.push(currentLine);
        currentLine = word;
      }
    });
    if (currentLine) lines.push(currentLine);

    return lines;
  };

  const formatDescription = (description) => {
    if (!description) return [];
    const words = description.split(' ');
    let lines = [];
    let currentLine = '';

    words.forEach(word => {
      if ((currentLine + ' ' + word).length <= 40) {
        currentLine += (currentLine ? ' ' : '') + word;
      } else {
        if (currentLine) lines.push(currentLine);
        currentLine = word;
      }
    });
    if (currentLine) lines.push(currentLine);

    return lines;
  };

  // Determine URL based on property type
  const getPropertyUrl = () => {
    const baseUrl = 'https://inkayros.com';
    const path = property?.operacion === 'alquiler' ? 'alquiler' : 'propiedad';
    return `${baseUrl}/${path}/${property?.id || 'casa-123'}`;
  };

  const features = [
    { name: `${property.habitaciones} Dormitorios`, category: 'left' },
    { name: `${property.banos} Baños`, category: 'left' },
    { name: `${property.area_total}m² Área`, category: 'left' },
    { name: 'Cochera', category: 'right' },
    { name: 'Jardín', category: 'right' }
  ];

  const titleLines = formatTitle(property.titulo);
  const descriptionLines = formatDescription(
    property.descripcion_corta || 'Descubre una elegante propiedad con acabados de primera.'
  );

  return (
    <div className="max-w-2xl bg-white shadow-lg rounded-lg overflow-hidden">
      {/* Header Section with Background Image */}
      <div className="relative h-[400px]">
        {/* Background Image */}
        <div className="absolute inset-0">
          <PropertyImage 
            url={property.imagenes[0]}
            alt="Main view"
            className="w-full h-full"
          />
        </div>

        {/* Enhanced Diagonal Overlay with Gradient */}
        <div className="absolute inset-0 bg-gradient-to-r from-blue-700 via-blue-600 to-transparent"
             style={{
               clipPath: 'polygon(0 0, 50% 0, 42% 100%, 0 100%)'
             }}>
          {/* Subtle pattern overlay for texture */}
          <div className="absolute inset-0 opacity-20 bg-[url('data:image/svg+xml;base64,PHN2ZyB4bWxucz0iaHR0cDovL3d3dy53My5vcmcvMjAwMC9zdmciIHdpZHRoPSI0MCIgaGVpZ2h0PSI0MCIgdmlld0JveD0iMCAwIDQwIDQwIj48ZyBmaWxsPSIjZmZmIiBmaWxsLW9wYWNpdHk9IjAuMSIgZmlsbC1ydWxlPSJldmVub2RkIj48cGF0aCBkPSJNMCAwaDQwdjQwSDB6TTIwIDIwaDIwdjIwSDIwek0wIDIwaDIwdjIwSDB6Ii8+PC9nPjwvc3ZnPg==')]"></div>
        </div>
        
        {/* Top status badge */}
        <div className="absolute top-4 left-6 z-20 bg-white bg-opacity-90 rounded-full px-4 py-1 text-blue-700 font-semibold text-sm shadow-md flex items-center">
          <Calendar className="h-4 w-4 mr-1" />
          <span>{property.operacion === 'alquiler' ? 'EN ALQUILER' : 'EN VENTA'}</span>
        </div>
        
        {/* Content - Left-aligned Title with improved structure */}
        <div className="relative z-10 p-8 flex flex-col justify-end h-full">
          <div className="ml-2">
            <div className="mb-1">
              <div className="h-1 w-16 bg-white mb-3 rounded"></div>
              <div className="space-y-2">
                {titleLines.map((line, index) => (
                  <h1 key={index} className="text-white text-3xl font-bold leading-tight drop-shadow-lg">
                    {line}
                  </h1>
                ))}
              </div>
            </div>
            
            {/* Optional subtitle */}
            <div className="mt-3 bg-blue-800 bg-opacity-40 inline-block px-3 py-1 rounded-md">
              <p className="text-white text-xs">
                {property.ubicacion}
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Price Section */}
      <div className="bg-gray-800 p-6">
        <div className="flex justify-between items-center">
          <div className="text-white ml-8">
            <div className="text-sm uppercase tracking-wider opacity-80">Precio</div>
            <div className="text-3xl font-bold">S/ {formatPrice(property.precio_soles)}</div>
            <div className="text-sm text-gray-400">USD {formatPrice(property.precio_dolares)}</div>
          </div>
          
          {/* QR Code with label */}
          <div className="mr-8 flex flex-col items-center">
            <div className="bg-white p-3 rounded-lg flex items-center justify-center shadow-lg">
              <QRCodeSVG value={getPropertyUrl()} size={64} />
            </div>
            <span className="text-white text-xs mt-1 opacity-80">Escanea para más info</span>
          </div>
        </div>
      </div>

      {/* Features Section */}
      <div className="p-8">
        <div className="grid grid-cols-2 gap-8">
          {/* Property Features */}
          <div>
            <h2 className="text-blue-600 text-xl font-semibold mb-4 border-b-2 border-blue-600 pb-2 flex items-center">
              <Info className="w-5 h-5 mr-2" />
              Características
            </h2>
            <p className="text-gray-600 text-sm mb-4">
              {property.descripcion?.slice(0, 100) || 'Una propiedad única con características excepcionales.'}
            </p>
            <div className="grid grid-cols-1 gap-4">
              {features.filter(f => f.category === 'left').map((feature, index) => (
                <div key={index} className="flex items-center gap-2">
                  <div className="w-5 h-5 rounded-full bg-blue-100 flex items-center justify-center">
                    <Check className="w-3 h-3 text-blue-600" />
                  </div>
                  <span className="text-gray-700">{feature.name}</span>
                </div>
              ))}
            </div>
          </div>

          {/* About Us */}
          <div>
            <h2 className="text-blue-600 text-xl font-semibold mb-4 border-b-2 border-blue-600 pb-2 flex items-center">
              <Star className="w-5 h-5 mr-2" />
              Sobre la propiedad:
            </h2>
            <p className="text-gray-600 text-sm mb-4">
            {property.descripcion_corta}
            </p>
            <div className="grid grid-cols-1 gap-4">
              {features.filter(f => f.category === 'right').map((feature, index) => (
                <div key={index} className="flex items-center gap-2">
                  <div className="w-5 h-5 rounded-full bg-blue-100 flex items-center justify-center">
                    <Check className="w-3 h-3 text-blue-600" />
                  </div>
                  <span className="text-gray-700">{feature.name}</span>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Image Gallery */}
        <div className="grid grid-cols-2 gap-4 mt-8">
          {property.imagenes.slice(1, 3).map((imagen, index) => (
            <div key={index} className="aspect-video border-blue-600 border-2 rounded-lg overflow-hidden shadow-md">
              <PropertyImage 
                url={imagen}
                alt={`Property view ${index + 1}`}
                className="w-full h-full"
              />
            </div>
          ))}
        </div>
      </div>

      {/* Enhanced Contact Section/Footer */}
      <div className="bg-gradient-to-r from-blue-700 to-blue-600 p-6 text-white">
        <div className="flex flex-col sm:flex-row items-start sm:items-center sm:justify-between">
          {/* Logo or company name */}
          <div className="flex items-center mb-4 sm:mb-0">
            <div className="bg-white p-2 rounded-full mr-3">
              <Home className="h-6 w-6 text-blue-600" />
            </div>
            <div>
              <h3 className="font-bold text-lg">InKayros.com</h3>
              <p className="text-xs text-blue-100">Inspira, innova, impacta.</p>
            </div>
          </div>
          
          {/* Contact information with icons */}
          <div className="text-sm">
            <div className="flex items-center gap-2 mb-2">
              <Mail className="h-4 w-4 text-blue-200" />
              <span>{property.vendedor?.email || 'contacto@inkayros.com'}</span>
            </div>
            <div className="flex items-center gap-2 mb-2">
              <Phone className="h-4 w-4 text-blue-200" />
              <span>{property.vendedor?.telefono|| '+51 123 456 789'}</span>
            </div>
            <div className="flex items-center gap-2">
              <MapPin className="h-4 w-4 text-blue-200" />
              <span>{property.ubicacion}</span>
            </div>
          </div>
        </div>
        
        {/* Website URL in footer */}
        <div className="mt-5 pt-4 border-t border-blue-500 text-center text-blue-100 text-sm">
          {getPropertyUrl()} • © {new Date().getFullYear()} Todos los derechos reservados
        </div>
      </div>
    </div>
  );
};

export default ElegantBlueFlyer;